import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import * as Icon from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { ApplicationUser } from "../models/user.model";
import { userService } from "../service/user.service";
import PageHeader from "./PageHeader";
import UserDrawer from "./UserDrawer";
import "./Users.css";

const Users: React.FC = () => {
  const [users, setUsers] = useState<ApplicationUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [selectedFirstName, setSelectedFirstName] =
    useState<ApplicationUser | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<ApplicationUser | null>(
    null
  );
  const [selectedRole, setSelectedRole] = useState<string | null>(null);

  const uniqueRolesList = React.useMemo(() => {
    const roles = users
      .filter((user) => user.roleName)
      .map((user) => user.roleName as string);
    return Array.from(new Set(roles));
  }, [users]);

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = localStorage.getItem("usersTablePage");
    return saved ? parseInt(saved) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const saved = localStorage.getItem("usersTableRows");
    return saved ? parseInt(saved) : 10;
  });
  const [sortField, setSortField] = useState<string>("FirstName");
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [createShowModal, setCreateShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApplicationUser | null>(
    null
  );
  const [createUser, setCreateUser] = useState<ApplicationUser | null>(null);
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";

  const fetchUsers = async () => {
    setLoading(true);
    setLastError(null);
    try {
      if (userRole === "RMCAdmin") {
        const res = await userService.getCurrentUsersFromApi();
        if (!res || !Array.isArray(res)) {
          throw new Error('Invalid response format from API');
        }
        const formattedUsers = res.map(user => ({
          ...user,
          id: user.id || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          roleName: user.roleName || '',
          roleId: user.roleId || '',
          isLocked: user.isLocked || false
        }));
        
        // Local sorting for RMCAdmin
        if (sortField && sortOrder) {
          formattedUsers.sort((a: any, b: any) => {
            const aValue = String(a[sortField.toLowerCase()] || '').toLowerCase();
            const bValue = String(b[sortField.toLowerCase()] || '').toLowerCase();
            return sortOrder === 1 
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          });
        }
        
        console.log('Fetched and sorted users:', {
          sortField,
          sortOrder,
          users: formattedUsers
        });
        setUsers(formattedUsers);
        setTotalRecords(formattedUsers.length);
      } else {
        // Use paginated endpoint with OrderBy
        const paginatedRequest = {
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          sortField: sortField,
          sortOrder: sortOrder === 1 ? "asc" : "desc",
          globalFilter: globalFilter,
        };
        const paginatedResult =
          await userService.getAllPaginated<ApplicationUser>(paginatedRequest);
        
        if (paginatedResult?.items) {
          const formattedUsers = (paginatedResult.items as ApplicationUser[]).map(user => ({
            ...user,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            roleName: user.roleName || ''
          }));
          console.log('Fetched paginated users:', formattedUsers);
          setUsers(formattedUsers);
        } else {
          setUsers([]);
        }
        setTotalRecords(paginatedResult?.totalCount || 0);
      }
    } catch (error) {
      console.error("❌ Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (userId: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the user.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await userService.delete(userId);
          fetchUsers();
          Swal.fire({
            toast: true,
            position: "bottom",
            icon: "success",
            title: "User deleted successfully.",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            customClass: { popup: "swal2-toast-bottom" },
          });
        } catch (error) {
          Swal.fire({
            toast: true,
            position: "bottom",
            icon: "error",
            title: "Failed to delete user.",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            customClass: { popup: "swal2-toast-bottom" },
          });
        }
      }
    });
  };
  useEffect(() => {
    if (userRole) {
      fetchUsers();
    }
  }, [userRole, currentPage, rowsPerPage, globalFilter, sortField, sortOrder]);

  const handleEditClick = (user: ApplicationUser) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCreateClick = () => {
    const newUser: ApplicationUser = {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      userName: "",
      phoneNumber: "",
      clientId: "",
      roleId: "",
    };
    setCreateUser(newUser);
    setCreateShowModal(true);
  };

  const actionBodyTemplate = (rowData: any) => (
    <div className="d-flex gap-2 align-items-center">
      <Button
        icon="pi pi-pencil"
        rounded
        severity="warning"
        onClick={(e) => {
          e.stopPropagation();
          handleEditClick(rowData);
        }}
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
      />
      <Button
        icon="pi pi-trash"
        rounded
        severity="danger"
        onClick={(e) => {
          e.stopPropagation();
          handleDelete(rowData.id);
        }}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
      />
    </div>
  );

  return (
    <>
      <div className="content">
        <PageHeader title="User List" showBackButton={false} />
        <div className="content-body">
          <div className="container-fluid mt-2">
            {lastError && (
              <div className="alert alert-danger" role="alert">
                {lastError}
              </div>
            )}
            <div className="row">
              <div className="col-md-12 mb-2">
                <div className="users-grid-container">
                  <DataTable
                    value={users}
                    loading={loading}
                    lazy
                    paginator
                    rows={rowsPerPage}
                    totalRecords={totalRecords}
                    first={currentPage * rowsPerPage}
                    onPage={(e) => {
                      const newPage = e.first / e.rows;
                      setCurrentPage(newPage);
                      setRowsPerPage(e.rows);
                      localStorage.setItem(
                        "usersTablePage",
                        newPage.toString()
                      );
                      localStorage.setItem("usersTableRows", e.rows.toString());
                    }}
                    sortField={sortField}
                    sortOrder={sortOrder === 1 ? 1 : -1}
                    onSort={(event) => {
                      const newSortField = event.sortField;
                      console.log('Sort event:', {
                        field: newSortField,
                        requestedOrder: event.sortOrder,
                        previousField: sortField,
                        previousOrder: sortOrder,
                        currentOrder: sortOrder === 1 ? 'asc' : 'desc'
                      });
                      
                      if (sortField === newSortField) {
                        // Toggle between ascending and descending only
                        setSortOrder(sortOrder === 1 ? -1 : 1);
                      } else {
                        // New column clicked, start with ascending
                        setSortField(newSortField);
                        setSortOrder(1);
                      }
                    }}
                    rowsPerPageOptions={[5, 10, 20]}
                    emptyMessage="No users found."
                    dataKey="id"
                    stripedRows
                    className="grid-table"
                    header={
                      <div className="d-flex justify-content-between align-items-end gap-3">
                        <div className="users-filter-container">
                          <div className="users-filter-item">
                            <IconField iconPosition="left">
                              <InputIcon>
                                <Icon.Search className="tx-12" />
                              </InputIcon>
                              <InputText
                                type="text"
                                value={globalFilter}
                                onChange={(e) => {
                                  setGlobalFilter(e.target.value);
                                  setSelectedFirstName(null);
                                  setSelectedEmail(null);
                                  setSelectedRole(null);
                                }}
                                placeholder="Search"
                                style={{ width: "200px", height: "38px" }}
                              />
                            </IconField>
                          </div>

                          <div className="users-filter-item">
                            <Dropdown
                              value={selectedFirstName}
                              onChange={(e) => {
                                setSelectedFirstName(e.value);
                                setGlobalFilter(
                                  e.value ? e.value.firstName : ""
                                );
                                setSelectedEmail(null);
                                setSelectedRole(null);
                              }}
                              options={users}
                              optionLabel="firstName"
                              placeholder="First Name"
                              style={{ width: "200px", height: "38px" }}
                              filter
                              showClear
                              appendTo="self"
                            />
                          </div>

                          <div className="users-filter-item">
                            <Dropdown
                              value={selectedEmail}
                              onChange={(e) => {
                                setSelectedEmail(e.value);
                                setGlobalFilter(e.value ? e.value.email : "");
                                setSelectedFirstName(null);
                                setSelectedRole(null);
                              }}
                              options={users}
                              optionLabel="email"
                              placeholder="Email"
                              style={{ width: "200px", height: "38px" }}
                              filter
                              showClear
                              appendTo="self"
                            />
                          </div>

                          <div className="users-filter-item">
                            <Dropdown
                              value={selectedRole}
                              onChange={(e) => {
                                setSelectedRole(e.value);
                                setGlobalFilter(e.value || "");
                                setSelectedFirstName(null);
                                setSelectedEmail(null);
                              }}
                              options={uniqueRolesList}
                              placeholder="Role Name"
                              style={{ width: "200px", height: "38px" }}
                              filter
                              showClear
                              appendTo="self"
                            />
                          </div>
                        </div>

                        <div className="users-action-buttons">
                          <button
                            type="button"
                            className="users-modern-btn btn btn-primary"
                            onClick={handleCreateClick}
                          >
                            <Icon.PlusCircle /> Create User
                          </button>
                        </div>
                      </div>
                    }
                  >
                    <Column
                      field="FirstName"
                      header="First Name"
                      sortable
                      sortField="FirstName"
                      body={(rowData) => rowData.firstName || '-'}
                    />
                    <Column
                      field="LastName"
                      header="Last Name"
                      sortable
                      sortField="LastName"
                      body={(rowData) => rowData.lastName || '-'}
                    />
                    <Column
                      field="Email"
                      header="Email"
                      sortable
                      sortField="Email"
                      body={(rowData) => rowData.email || '-'}
                    />
                    <Column
                      field="PhoneNumber"
                      header="Phone"
                      sortable
                      sortField="PhoneNumber"
                      body={(rowData) => rowData.phoneNumber || '-'}
                    />
                    <Column
                      field="RoleName"
                      header="Role Name"
                      sortable
                      sortField="RoleName"
                      body={(rowData) => rowData.roleName || '-'}
                    />
                    <Column
                      header="Actions"
                      body={actionBodyTemplate}
                      style={{ minWidth: "100px" }}
                    />
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showModal && selectedUser && (
        <UserDrawer
          show={showModal}
          onHide={() => setShowModal(false)}
          user={selectedUser}
          onSuccess={fetchUsers}
          mode="edit"
        />
      )}
      {createShowModal && createUser && (
        <UserDrawer
          show={createShowModal}
          onHide={() => setCreateShowModal(false)}
          user={createUser}
          onSuccess={fetchUsers}
          mode="create"
        />
      )}
    </>
  );
};
export default Users;
